import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// ====== Schemas from generated OpenAPI spec ======

const { WriteoffFirestoreResponse, CreateWriteoffDto, WriteoffItemFirestoreResponse } = schemas;

export const WriteoffResponseSchema = WriteoffFirestoreResponse;
export const CreateWriteoffSchema = CreateWriteoffDto;
export const WriteoffItemSchema = WriteoffItemFirestoreResponse;

export const WriteoffComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: WriteoffResponseSchema.nullable(),
}).passthrough();

// ====== Type Exports ======

export type WriteoffResponse = z.infer<typeof WriteoffResponseSchema>;
export type CreateWriteoff = z.infer<typeof CreateWriteoffSchema>;
export type WriteoffItem = z.infer<typeof WriteoffItemSchema>;
export type WriteoffComparisonResult = z.infer<typeof WriteoffComparisonResultSchema>;

/**
 * Create writeoffs API factory
 */
export function createWriteoffsApi(client: TypedApiClient) {
  return {
    /**
     * Get writeoffs with optional filters
     */
    getWriteoffs: (params?: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      productId?: string;
      limit?: number;
      offset?: number;
    }): Promise<WriteoffResponse[]> => {
      return client.get('/writeoffs', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(WriteoffResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/writeoffs` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getWriteoffsWindow: (
      params: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      productId?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof WriteoffResponseSchema>>> => {
      return client.get('/writeoffs/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(WriteoffResponseSchema),
      });
    },

    /**
     * Get a single writeoff by ID
     */
    getWriteoffById: (id: string): Promise<WriteoffResponse> => {
      return client.get(apiPath`/writeoffs/${id}`, {
        responseSchema: WriteoffResponseSchema,
      });
    },

    /**
     * Create a new writeoff
     */
    createWriteoff: (data: CreateWriteoff): Promise<WriteoffResponse> => {
      return client.post('/writeoffs', {
        data,
        requestSchema: CreateWriteoffSchema,
        responseSchema: WriteoffResponseSchema,
      });
    },

    /**
     * Delete a writeoff
     */
    deleteWriteoff: (id: string) => {
      return client.delete(apiPath`/writeoffs/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Compare writeoff with Firestore for dual-write verification
     */
    compareWithFirestore: (firestoreId: string): Promise<WriteoffComparisonResult> => {
      return client.get(apiPath`/writeoffs/compare/${firestoreId}`, {
        responseSchema: WriteoffComparisonResultSchema,
      });
    },
  };
}
